import { useState } from "react";
import { useLang } from "../context/LanguageContext";
import { useAuth } from "../context/AuthContext";
import { LANGUAGES } from "../i18n/translations";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, Globe, ChevronDown, Facebook, Instagram } from "lucide-react";
import { SITE } from "../data/site";

export function Navbar() {
  const [open, setOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const { pathname } = useLocation();
  const { t, lang, setLang } = useLang();
  const current = LANGUAGES.find((l) => l.code === lang) || LANGUAGES[1];

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/motorcycles", label: t.nav.motorcycles },
    { to: "/reserve", label: t.nav.reserve },
    { to: "/gallery", label: t.nav.gallery },
    { to: "/locations", label: t.nav.locations },
    { to: "/blog", label: t.nav.blog },
    { to: "/services/car-wash", label: t.nav.carwash },
    { to: "/services/maintenance", label: t.nav.maintenance },
  ];

  const pick = (code) => { setLang(code); setLangOpen(false); setOpen(false); };

  const Socials = ({ className = "" }) => (
    <div className={`flex items-center gap-2 ${className}`}>
      <a href={SITE.facebook} target="_blank" rel="noreferrer" data-testid="nav-facebook" aria-label="Facebook"
        className="text-brand-gray hover:text-white hover:bg-brand-blue border border-slate-200 hover:border-brand-blue rounded-full p-1.5 transition-colors">
        <Facebook size={16} />
      </a>
      <a href={SITE.instagram} target="_blank" rel="noreferrer" data-testid="nav-instagram" aria-label="Instagram"
        className="text-brand-gray hover:text-white hover:bg-brand-orange border border-slate-200 hover:border-brand-orange rounded-full p-1.5 transition-colors">
        <Instagram size={16} />
      </a>
    </div>
  );

  const LangDropdown = ({ panelClass = "" }) => (
    <div className="relative">
      <button
        onClick={() => setLangOpen((o) => !o)}
        data-testid="lang-toggle"
        className="inline-flex items-center gap-1.5 font-heading font-bold text-sm rounded-full border border-slate-200 px-3 py-1.5 text-brand-gray hover:border-brand-blue hover:text-brand-blue transition-colors"
      >
        <Globe size={15} /> <span className="text-base leading-none">{current.flag}</span> {current.short}
        <ChevronDown size={14} className={`transition-transform ${langOpen ? "rotate-180" : ""}`} />
      </button>
      {langOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setLangOpen(false)} />
          <div className={`absolute z-50 mt-2 w-52 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden ${panelClass}`} data-testid="lang-menu">
            {LANGUAGES.map((l) => (
              <button
                key={l.code}
                onClick={() => pick(l.code)}
                data-testid={`lang-option-${l.code}`}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors ${
                  l.code === lang ? "bg-brand-orange text-white" : "text-brand-gray hover:bg-slate-50"
                }`}
              >
                <span className="text-xl leading-none">{l.flag}</span>
                <span className="font-heading font-bold text-sm flex-1">{l.label}</span>
                <span className={`text-xs font-bold ${l.code === lang ? "text-white/80" : "text-slate-400"}`}>{l.short}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );

  return (
    <header className="sticky top-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-20">
        <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="navbar-logo">
          <img src={SITE.logo} alt="Nica Moto Rentals" className="h-12 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5">
          {links.map((l) => (
            <Link
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to}`}
              className={`font-heading font-semibold text-sm tracking-wide transition-colors ${
                pathname === l.to ? "text-brand-orange" : "text-brand-gray hover:text-brand-blue"
              }`}
            >
              {l.label}
            </Link>
          ))}
          <Socials />
          <LangDropdown panelClass="right-0" />
          <a href={`tel:${SITE.phone}`} data-testid="nav-phone" className="hidden xl:inline-flex items-center gap-2 bg-brand-blue hover:bg-brand-blue/90 text-white font-heading font-bold text-sm py-2.5 px-5 rounded-full transition-colors">
            <Phone size={16} /> {SITE.phone}
          </a>
        </nav>

        <div className="lg:hidden flex items-center gap-3">
          <Socials />
          <LangDropdown panelClass="right-0" />
          <button className="text-brand-gray" onClick={() => setOpen((o) => !o)} data-testid="nav-mobile-toggle" aria-label="Toggle menu">
            {open ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-100 bg-white px-4 py-4 space-y-1">
          {links.map((l) => (
            <Link key={l.to} to={l.to} onClick={() => setOpen(false)} className="block py-2.5 font-heading font-semibold text-brand-gray" data-testid={`nav-mobile-${l.to}`}>
              {l.label}
            </Link>
          ))}
          <a href={`tel:${SITE.phone}`} className="block py-2.5 font-heading font-bold text-brand-blue">{SITE.phone}</a>
        </div>
      )}
    </header>
  );
}

export default Navbar;
